import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { ApiService } from '../Services/APIService';
import { StorageUtils } from '../Utils/StorageUtils';

const UserProfileContext = createContext();

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Debounce duration for storage events (300ms)
const DEBOUNCE_DURATION = 300;

export const UserProfileProvider = ({ children }) => {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  // Track in-flight request to prevent duplicates
  const inFlightRequest = useRef(null);
  
  // Debounce timer ref
  const debounceTimer = useRef(null);
  
  // Cache for image URLs with timestamps to prevent repeated requests
  const imageUrlCache = useRef(new Map());

  /**
   * Fetch profile data from API with caching and deduplication
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   */
  const fetchProfile = useCallback(async (forceRefresh = false) => {
    const userData = StorageUtils.getUserData();
    
    // Check if user is logged in
    if (!userData || !userData.user || !userData.user.id) {
      console.log('👤 UserProfileContext: No user data, skipping profile fetch');
      setProfileData(null);
      return null;
    }

    // Check cache validity (unless force refresh)
    if (!forceRefresh && lastFetchTime && profileData) {
      const cacheAge = Date.now() - lastFetchTime;
      if (cacheAge < CACHE_DURATION) {
        console.log(`✅ UserProfileContext: Using cached profile data (age: ${Math.round(cacheAge / 1000)}s)`);
        return profileData;
      }
    }

    // If there's already a request in flight, wait for it instead of making a new one
    if (inFlightRequest.current) {
      console.log('⏳ UserProfileContext: Request already in flight, waiting...');
      try {
        const result = await inFlightRequest.current;
        return result;
      } catch (error) {
        console.error('❌ UserProfileContext: In-flight request failed:', error);
        return null;
      }
    }

    // Create new request
    console.log('🔄 UserProfileContext: Fetching fresh profile data from API...');
    setIsLoading(true);

    const requestPromise = (async () => {
      try {
        ApiService.setAuthToken(userData.token);
        const response = await ApiService.get('users/profile');
        
        if (response.data) {
          const freshData = response.data;
          console.log('✅ UserProfileContext: Fresh profile data received');
          
          // Clear image cache when profile data changes (new image might be uploaded)
          imageUrlCache.current.clear();
          
          // Update state with fresh data
          setProfileData(freshData);
          setLastFetchTime(Date.now());
          
          return freshData;
        } else {
          console.warn('⚠️ UserProfileContext: API returned no data');
          return null;
        }
      } catch (error) {
        console.error('❌ UserProfileContext: Failed to fetch profile data:', error);
        
        // If we have cached data, keep using it even if fetch fails
        if (profileData) {
          console.log('ℹ️ UserProfileContext: Using stale cache due to fetch error');
          return profileData;
        }
        
        return null;
      } finally {
        setIsLoading(false);
        inFlightRequest.current = null;
      }
    })();

    // Store the promise so other calls can wait for it
    inFlightRequest.current = requestPromise;
    
    return requestPromise;
  }, [lastFetchTime, profileData]);

  /**
   * Force refresh profile data (called after profile updates)
   */
  const refreshProfile = useCallback(async () => {
    console.log('🔄 UserProfileContext: Force refreshing profile...');
    return await fetchProfile(true);
  }, [fetchProfile]);

  /**
   * Debounced handler for storage events
   */
  const handleStorageChange = useCallback(() => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      console.log('📡 UserProfileContext: Storage event detected (debounced) - refreshing profile...');
      fetchProfile(true);
    }, DEBOUNCE_DURATION);
  }, [fetchProfile]);

  // Initial fetch on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Listen for storage events (debounced)
  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [handleStorageChange]);

  // Helper to get user data with proper defaults
  const getUserData = useCallback(() => {
    if (!profileData || !profileData.user) {
      return {
        id: '',
        name: '',
        email: '',
        image: '/assets/default_user.png'
      };
    }

    const user = profileData.user;
    const profileImageUrl = user.profile_image;
    
    // Use cached image URL or create one with timestamp (only once per profile update)
    let imageUrl = '/assets/default_user.png';
    if (profileImageUrl && profileImageUrl !== '/assets/default_user.png') {
      // Check if we already have a cached URL for this profile image
      if (imageUrlCache.current.has(profileImageUrl)) {
        imageUrl = imageUrlCache.current.get(profileImageUrl);
      } else {
        // Create new URL with timestamp and cache it
        const separator = profileImageUrl.includes('?') ? '&' : '?';
        imageUrl = `${profileImageUrl}${separator}t=${Date.now()}`;
        imageUrlCache.current.set(profileImageUrl, imageUrl);
      }
    }

    return {
      id: user.id || '',
      name: user.name || '',
      email: user.email || '',
      image: imageUrl,
      subscription: user.subscription || null
    };
  }, [profileData]);

  const value = {
    // State
    profileData,
    isLoading,
    
    // Methods
    fetchProfile,
    refreshProfile,
    getUserData,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};

